
import React, { useState, useEffect } from 'react';
import { useAppContext } from '../../contexts/AppContext';

// Removed firebase imports as we are in Local Storage mode
import { Activity, Database, CheckCircle, XCircle, PlayCircle, Server, RefreshCw } from 'lucide-react';

// --- Types ---
// --- Types ---
interface ConnectionStatus {
    status: 'connected' | 'disconnected' | 'checking' | 'partial';
    latency: number | null;
    lastChecked: Date | null;
    error?: string;
    details?: {
        fmeaItems: 'ok' | 'fail';
        users: 'ok' | 'fail';
        activityLogs: 'ok' | 'fail';
        errors: string[];
    };
}

interface TestResult {
    id: string;
    name: string;
    status: 'pending' | 'running' | 'passed' | 'failed';
    message?: string;
    duration?: number;
}

// --- Connectivity Checker Component (Local Mode) ---
const ConnectionMonitor: React.FC = () => {
    const { uploadMockData, dataSourceMode } = useAppContext();
    const [connStatus, setConnStatus] = useState<ConnectionStatus>({ status: 'checking', latency: null, lastChecked: null });

    const checkConnection = async () => {
        setConnStatus(prev => ({ ...prev, status: 'checking' }));
        const start = performance.now();
        const details: ConnectionStatus['details'] = { fmeaItems: 'fail', users: 'fail', activityLogs: 'fail', errors: [] };

        try {
            // Test 1: Local Storage Availability
            const isLocalAvailable = typeof localStorage !== 'undefined';

            if (isLocalAvailable) {
                // Check Data integrity in local storage
                const dfrData = localStorage.getItem('magnus_dfr_data');
                if (dfrData) {
                    try {
                        JSON.parse(dfrData);
                        details.fmeaItems = 'ok';
                    } catch {
                        details.errors.push("Local Storage 'magnus_dfr_data' is corrupt");
                    }
                } else {
                    details.errors.push("Local Storage 'magnus_dfr_data' is missing (Try Re-Seed)");
                }

                const usersData = localStorage.getItem('magnus_users');
                if (usersData) {
                    try {
                        JSON.parse(usersData);
                        details.users = 'ok';
                    } catch { details.errors.push("Local Storage 'magnus_users' is corrupt"); }
                } else {
                    // Check if default mock users are loaded in context - effectively OK for local mode if not persisted yet
                    details.users = 'ok';
                }

                details.activityLogs = 'ok'; // Activity logs are fire-and-forget in local, assume OK
            } else {
                details.errors.push("Window.localStorage is not available");
            }

            const end = performance.now();

            // Determine overall status
            let overall: ConnectionStatus['status'] = 'disconnected';
            if (details.fmeaItems === 'ok' && details.users === 'ok') overall = 'connected';
            else if (details.fmeaItems === 'ok' || details.users === 'ok') overall = 'partial';

            setConnStatus({
                status: overall,
                latency: Math.round(end - start),
                lastChecked: new Date(),
                error: details.errors.length > 0 ? details.errors[0] : undefined,
                details
            });

        } catch (error: any) {
            console.error("Connection check failed:", error);
            setConnStatus({
                status: 'disconnected',
                latency: null,
                lastChecked: new Date(),
                error: error.message
            });
        }
    };

    useEffect(() => {
        checkConnection();
    }, []);

    return (
        <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Database className="text-primary" /> Data Persistence ({dataSourceMode})
            </h2>

            <div className="flex flex-col gap-4 bg-background p-4 rounded-lg border border-border">
                <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-full ${connStatus.status === 'connected' ? 'bg-green-100 text-green-600' :
                        connStatus.status === 'checking' ? 'bg-yellow-100 text-yellow-600' :
                            connStatus.status === 'partial' ? 'bg-orange-100 text-orange-600' : 'bg-red-100 text-red-600'
                        }`}>
                        <Server size={24} />
                    </div>
                    <div>
                        <div className="font-semibold text-text-primary text-lg">
                            {connStatus.status === 'connected' ? 'Local Storage Active' :
                                connStatus.status === 'checking' ? 'Checking Storage...' :
                                    connStatus.status === 'partial' ? 'Partial Data Missing' : 'Storage Failed'}
                        </div>
                        <div className="text-sm text-text-secondary">
                            {connStatus.lastChecked ? `Last checked: ${connStatus.lastChecked.toLocaleTimeString()}` : 'Initializing...'}
                        </div>
                    </div>
                </div>

                {/* Granular Details */}
                {connStatus.details && (
                    <div className="grid grid-cols-3 gap-2 mt-2">
                        <div className={`text-xs px-2 py-1 rounded border ${connStatus.details.fmeaItems === 'ok' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
                            DFR Data: {connStatus.details.fmeaItems.toUpperCase()}
                        </div>
                        <div className={`text-xs px-2 py-1 rounded border ${connStatus.details.users === 'ok' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
                            Users Data: {connStatus.details.users.toUpperCase()}
                        </div>
                        <div className={`text-xs px-2 py-1 rounded border ${connStatus.details.activityLogs === 'ok' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
                            System Logs: {connStatus.details.activityLogs.toUpperCase()}
                        </div>
                    </div>
                )}

                {(connStatus.status === 'disconnected' || connStatus.status === 'partial') && (
                    <div className="mt-2 p-3 bg-red-50 border border-red-100 rounded text-xs font-mono text-red-600 overflow-x-auto">
                        <div className="font-bold mb-1">Errors Detected:</div>
                        {connStatus.details?.errors.map((err, i) => (
                            <div key={i}>{err}</div>
                        )) || connStatus.error}
                    </div>
                )}
            </div>

            <div className="grid grid-cols-2 gap-4 mt-4">
                <button
                    onClick={checkConnection}
                    className="w-full py-2 bg-secondary/10 hover:bg-secondary/20 text-secondary rounded transition-colors text-sm font-medium"
                >
                    Re-Check Storage
                </button>
                <button
                    onClick={uploadMockData}
                    className="w-full py-2 flex items-center justify-center gap-2 bg-amber-500/10 hover:bg-amber-500/20 text-amber-600 rounded transition-colors text-sm font-medium"
                >
                    <RefreshCw size={14} /> Re-Seed Local Data
                </button>
            </div>
        </div>
    );
};

// --- In-App Unit Test Runner ---
const UnitTestRunner: React.FC = () => {
    const { allData, users, currentUser } = useAppContext();
    const [tests, setTests] = useState<TestResult[]>([
        { id: '1', name: 'Data Load Integrity', status: 'pending' },
        { id: '2', name: 'FMEA-P-Diagram Linkage', status: 'pending' },
        { id: '3', name: 'PBS Hierarchy Valid', status: 'pending' },
        { id: '4', name: 'DVP&R Traceability', status: 'pending' },
        { id: '5', name: 'RPN Calculation Accuracy', status: 'pending' },
        { id: '6', name: 'Control Effectiveness Range', status: 'pending' },
        { id: '7', name: 'Customer Requirements Links', status: 'pending' },
        { id: '8', name: 'BOM-FMEA Consistency', status: 'pending' },
        { id: '9', name: 'User Permissions Valid', status: 'pending' },
        { id: '10', name: 'Materials Database Schema', status: 'pending' }
    ]);

    const runTests = async () => {
        const newTests = [...tests].map(t => ({ ...t, status: 'running' as const, message: undefined }));
        setTests(newTests);

        // Simulate async execution for visual feedback
        const runAssertion = async (index: number, check: () => void) => {
            await new Promise(r => setTimeout(r, 400)); // Visual delay
            setTests(prev => {
                const updated = [...prev];
                const start = performance.now();
                try {
                    check();
                    updated[index] = { ...updated[index], status: 'passed', duration: Math.round(performance.now() - start) };
                } catch (e: any) {
                    updated[index] = { ...updated[index], status: 'failed', message: e.message, duration: Math.round(performance.now() - start) };
                }
                return updated;
            });
        };

        // TEST 1: Data Integrity
        await runAssertion(0, () => {
            if (!allData) throw new Error("Data context is null");
            if (allData.fmeaItems.length === 0 && allData.bomItems.length === 0) throw new Error("Database appears empty (or mock data missing)");
        });

        // TEST 2: FMEA Linkage
        await runAssertion(1, () => {
            const orphans = allData.fmeaItems.filter(f => !allData.pDiagramList.some(p => p.consolidatedFunctionId === f.function));
            if (orphans.length > 0) throw new Error(`${orphans.length} FMEA items have invalid Function IDs (Orphaned)`);
        });

        // TEST 3: PBS Valid
        await runAssertion(2, () => {
            // Basic check: do we have BOM items?
            if (allData.bomItems.length === 0) throw new Error("No BOM items found to build PBS.");
        });

        // TEST 4: Traceability
        await runAssertion(3, () => {
            const brokenLinks = allData.dvprTests.filter(t =>
                t.traceableRiskId?.some(riskId => !allData.fmeaItems.some(f => f.id === riskId))
            );
            if (brokenLinks.length > 0) throw new Error(`${brokenLinks.length} DVP&R tests link to non-existent FMEA IDs`);
        });

        // TEST 5: RPN Calculation Accuracy
        await runAssertion(4, () => {
            const invalidRPNs = allData.fmeaItems.filter(f => {
                const calculated = f.S * f.O * f.D;
                return f.RPN !== calculated;
            });
            if (invalidRPNs.length > 0) throw new Error(`${invalidRPNs.length} FMEA items have incorrect RPN (S×O×D ≠ RPN)`);
        });

        // TEST 6: Control Effectiveness (Detection ratings should be reasonable)
        await runAssertion(5, () => {
            const invalidDetection = allData.fmeaItems.filter(f => f.D < 1 || f.D > 10);
            if (invalidDetection.length > 0) throw new Error(`${invalidDetection.length} FMEA items have invalid Detection rating (must be 1-10)`);

            const criticalNoDetection = allData.fmeaItems.filter(f => f.S >= 9 && f.D >= 8);
            if (criticalNoDetection.length > 5) throw new Error(`${criticalNoDetection.length} critical failures (S≥9) lack detection controls (D≥8)`);
        });

        // TEST 7: Customer Requirements Linkage
        await runAssertion(6, () => {
            if (allData.customerRequirements && allData.customerRequirements.length > 0) {
                const orphanedReqs = allData.customerRequirements.filter(req =>
                    !allData.productFeatures?.some(feat => feat.linkedCustomerReqIds?.includes(req.id))
                );
                if (orphanedReqs.length > 0) throw new Error(`${orphanedReqs.length} customer requirements not linked to any product features`);
            }
        });

        // TEST 8: BOM-FMEA Consistency
        await runAssertion(7, () => {
            const bomComponentNames = new Set(allData.bomItems.map(b => b.component));
            const fmeaFunctions = new Set(allData.fmeaItems.map(f => f.itemFunction));

            // Check for reasonable overlap between BOM and FMEA
            const totalUnique = new Set([...bomComponentNames, ...fmeaFunctions]).size;
            const hasReasonableOverlap = totalUnique < (bomComponentNames.size + fmeaFunctions.size) * 1.5;
            if (!hasReasonableOverlap) throw new Error(`BOM and FMEA seem disconnected (no component overlap)`);
        });

        // TEST 9: User Permissions Valid
        await runAssertion(8, () => {
            if (!users || users.length === 0) throw new Error("No users loaded from database");

            const invalidPerms = users.filter(u =>
                !u.permissions ||
                !Array.isArray(u.permissions) ||
                (!u.permissions.includes('*') && u.permissions.length === 0 && u.role === 'user')
            );
            if (invalidPerms.length > 0) throw new Error(`${invalidPerms.length} users have invalid permission structures`);

            if (!currentUser) throw new Error("No current user logged in");
        });

        // TEST 10: Materials Database Schema
        await runAssertion(9, () => {
            if (allData.materials && allData.materials.length > 0) {
                const invalidMaterials = allData.materials.filter((m: any) =>
                    !m.id || !m.name || !m.category
                );
                if (invalidMaterials.length > 0) throw new Error(`${invalidMaterials.length} materials missing required fields (id, name, category)`);
            }
        });
    };

    return (
        <div className="bg-card border border-border rounded-lg p-6 shadow-sm flex flex-col h-full">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold flex items-center gap-2">
                    <Activity className="text-primary" /> Live System Health Checks
                </h2>
                <button
                    onClick={runTests}
                    className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors shadow-sm"
                >
                    <PlayCircle size={18} /> Run All Tests
                </button>
            </div>

            <div className="space-y-3 flex-grow overflow-y-auto">
                {tests.map(test => (
                    <div key={test.id} className="flex items-center justify-between p-3 bg-background border border-border rounded-md">
                        <div className="flex items-center gap-3">
                            {test.status === 'pending' && <div className="w-5 h-5 rounded-full border-2 border-text-secondary/30" />}
                            {test.status === 'running' && <LoaderIcon />}
                            {test.status === 'passed' && <CheckCircle size={20} className="text-green-500" />}
                            {test.status === 'failed' && <XCircle size={20} className="text-red-500" />}

                            <div>
                                <div className="font-medium text-text-primary">{test.name}</div>
                                {test.message && <div className="text-xs text-red-500 mt-0.5">{test.message}</div>}
                            </div>
                        </div>
                        <div className="text-xs text-text-secondary font-mono">
                            {test.duration !== undefined ? `${test.duration}ms` : '-'}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const LoaderIcon = () => (
    <svg className="animate-spin h-5 w-5 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
);

// --- Main Page ---
const TroubleshootingPage: React.FC = () => {
    return (
        <div className="space-y-6 max-w-6xl mx-auto pb-10">
            <div>
                <h1 className="text-3xl font-bold text-text-primary">System Troubleshooting</h1>
                <p className="text-text-secondary mt-1">
                    Monitor database connectivity and verify system integrity.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <ConnectionMonitor />
                <UnitTestRunner />
            </div>
        </div>
    );
};

export default TroubleshootingPage;
